import type { FaunaCatalog } from '@/fauna/FaunaCatalog';
import {
  defaultPose,
  faunaClassLabel,
  faunaAttachmentLabel,
  type FaunaClass,
  type FaunaDefinition,
  type FaunaPose,
} from '@/fauna/FaunaDefinition';
import { clear, el, formatLength, setClass } from './dom';
import { icon } from './Icons';
import { faunaThumbnailSvg } from './FaunaThumbnail';
import type { UiPreferences } from './UiPreferences';

/**
 * The fauna palette — the second of the three placeable classes in the left dock.
 *
 * Deliberately a smaller instrument than `FloraBrowser`. That palette has nine facet groups, six
 * sorts, four groupings and a site-suitability rating because a flora catalog is a decision with
 * consequences: the wrong plant dies. Choosing a rabbit is not that decision. Search, class
 * sections, and a pose to place it in is the whole of what a designer needs, and adding filters
 * nobody asked for would make the smaller job look like the larger one.
 *
 * ## Pose is picked here, not after placing
 *
 * The one control this panel does have that the flora palette does not. A pose changes the
 * creature's height by more than any other choice — a sitting cottontail is 200 mm and an alert one
 * 280 — so it belongs to the act of arming the tool, in the same gesture as choosing the species.
 * Placing first and re-posing afterwards would mean every placement lands at the wrong height once.
 */

export interface FaunaBrowserDeps {
  catalog: FaunaCatalog;
  preferences: UiPreferences;
  /** Arm placement with a record and a pose. */
  onPick: (definitionId: string, poseId: string) => void;
  /** What is armed right now, so the palette can show it selected. */
  armed?: () => { definitionId: string; poseId: string } | null;
}

export class FaunaBrowser {
  readonly root: HTMLElement;

  private search = '';
  private activeClass: FaunaClass | 'all' = 'all';
  /** Chosen pose per record, so re-picking a species keeps the pose you last used for it. */
  private readonly poseChoice = new Map<string, string>();

  private readonly searchInput: HTMLInputElement;
  private readonly railEl: HTMLElement;
  private readonly resultsEl: HTMLElement;
  private readonly countEl: HTMLElement;
  private readonly body: HTMLElement;

  constructor(private readonly deps: FaunaBrowserDeps) {
    this.countEl = el('span', { class: 'lg-panel__count' });
    this.searchInput = el('input', {
      class: 'lg-search__input',
      attrs: {
        type: 'search',
        placeholder: 'Search creatures, species, tags…',
        'aria-label': 'Search the fauna catalog',
      },
      on: {
        input: () => {
          this.search = this.searchInput.value;
          this.renderResults();
        },
      },
    });

    this.railEl = el('div', {
      class: 'lg-rail',
      attrs: { role: 'group', 'aria-label': 'Fauna class' },
    });
    this.resultsEl = el('div', { class: 'lg-browser__results' });

    this.body = el(
      'div',
      { class: 'lg-panel__body' },
      el(
        'div',
        { class: 'lg-search' },
        el('span', { class: 'lg-search__icon', html: icon('search', 16) }),
        this.searchInput,
      ),
      this.railEl,
      this.resultsEl,
    );

    this.root = el(
      'aside',
      {
        class: 'lg-panel lg-panel--left lg-panel--palette',
        attrs: { 'aria-label': 'Fauna palette' },
      },
      el(
        'div',
        { class: 'lg-panel__head' },
        el('span', { class: 'lg-panel__icon', html: icon('fauna', 16) }),
        el('h2', { class: 'lg-panel__title', text: 'Fauna' }),
        this.countEl,
        el('button', {
          class: 'lg-panel__collapse',
          html: icon('chevron', 16),
          title: 'Shut the fauna palette',
          attrs: { type: 'button', 'aria-label': 'Shut the fauna palette' },
          on: { click: () => this.setShut(!this.isShut) },
        }),
      ),
      this.body,
    );

    this.setShut(deps.preferences.panelShut('fauna'));
    this.renderRail();
    this.renderResults();
  }

  get isShut(): boolean {
    return this.root.classList.contains('is-shut');
  }

  setShut(shut: boolean): void {
    setClass(this.root, 'is-shut', shut);
    this.deps.preferences.setPanelShut('fauna', shut);
  }

  /** Re-render what depends on outside state — which record is armed, and the unit. */
  refresh(): void {
    this.renderResults();
  }

  /* ---------------------------------- Rendering ---------------------------------- */

  private renderRail(): void {
    clear(this.railEl);
    const classes = this.deps.catalog.classes();
    const chip = (label: string, value: FaunaClass | 'all', count: number): HTMLElement =>
      el(
        'button',
        {
          class: `lg-chip${this.activeClass === value ? ' is-active' : ''}`,
          attrs: { type: 'button', 'aria-pressed': this.activeClass === value },
          on: {
            click: () => {
              this.activeClass = value;
              this.renderRail();
              this.renderResults();
            },
          },
        },
        el('span', { text: label }),
        el('span', { class: 'lg-chip__count', text: String(count) }),
      );

    this.railEl.appendChild(chip('All', 'all', this.deps.catalog.size));
    for (const faunaClass of classes) {
      this.railEl.appendChild(
        chip(faunaClassLabel(faunaClass), faunaClass, this.deps.catalog.byClass(faunaClass).length),
      );
    }
  }

  private matches(): FaunaDefinition[] {
    const found = this.deps.catalog.search(this.search);
    if (this.activeClass === 'all') return found;
    return found.filter((definition) => definition.class === this.activeClass);
  }

  private renderResults(): void {
    clear(this.resultsEl);
    const results = this.matches();
    this.countEl.textContent = String(results.length);

    if (results.length === 0) {
      this.resultsEl.appendChild(
        el('div', { class: 'lg-empty' }, el('p', { text: 'Nothing matches that.' })),
      );
      return;
    }

    // Grouped by class whenever more than one is on screen — a flat grid of a deer beside a
    // bumblebee, both drawn at tile size, is the one arrangement that actively hides scale.
    const groups = new Map<FaunaClass, FaunaDefinition[]>();
    for (const definition of results) {
      const list = groups.get(definition.class) ?? [];
      list.push(definition);
      groups.set(definition.class, list);
    }

    for (const [faunaClass, members] of groups) {
      const section = el('section', { class: 'lg-group' });
      if (groups.size > 1) {
        section.appendChild(
          el(
            'h3',
            { class: 'lg-group__title' },
            el('span', { text: faunaClassLabel(faunaClass) }),
            el('span', { class: 'lg-group__count', text: String(members.length) }),
          ),
        );
      }
      const grid = el('div', { class: 'lg-grid' });
      for (const definition of members) grid.appendChild(this.tile(definition));
      section.appendChild(grid);
      this.resultsEl.appendChild(section);
    }
  }

  private tile(definition: FaunaDefinition): HTMLElement {
    const pose = this.poseFor(definition);
    const armed = this.deps.armed?.() ?? null;
    const isArmed = armed?.definitionId === definition.id;

    const tile = el(
      'button',
      {
        class: `lg-tile${isArmed ? ' is-active' : ''}`,
        title: this.tooltip(definition, pose),
        attrs: { type: 'button' },
        on: { click: () => this.pick(definition, pose.id) },
      },
      el('span', {
        class: 'lg-tile__art',
        html: faunaThumbnailSvg(definition, pose),
      }),
      el('span', { class: 'lg-tile__name', text: definition.name }),
      el('span', {
        class: 'lg-tile__meta',
        text: this.sizeLine(definition, pose),
      }),
    );

    if (definition.poses.length > 1) tile.appendChild(this.poseRow(definition, pose));
    return tile;
  }

  /**
   * The pose strip under a tile.
   *
   * A row of small buttons rather than a `<select>`, because the choice is between two and five
   * options that each mean a picture — and because a select inside a tile that is itself a button is
   * a keyboard trap waiting to happen. Clicks are stopped from reaching the tile so choosing a pose
   * does not also arm the tool with the previous one.
   */
  private poseRow(definition: FaunaDefinition, active: FaunaPose): HTMLElement {
    const row = el('span', { class: 'lg-poses' });
    for (const pose of definition.poses) {
      row.appendChild(
        el('span', {
          class: `lg-poses__item${pose.id === active.id ? ' is-active' : ''}`,
          text: pose.name,
          title: `${pose.name} — ${faunaAttachmentLabel(pose.attachment ?? definition.attachment[0] ?? 'ground')}`,
          attrs: { role: 'button', tabindex: 0 },
          on: {
            click: (event: MouseEvent) => {
              event.stopPropagation();
              this.poseChoice.set(definition.id, pose.id);
              this.pick(definition, pose.id);
            },
            keydown: (event: KeyboardEvent) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              event.stopPropagation();
              this.poseChoice.set(definition.id, pose.id);
              this.pick(definition, pose.id);
            },
          },
        }),
      );
    }
    return row;
  }

  private pick(definition: FaunaDefinition, poseId: string): void {
    this.poseChoice.set(definition.id, poseId);
    this.deps.onPick(definition.id, poseId);
    this.renderResults();
  }

  private poseFor(definition: FaunaDefinition): FaunaPose {
    const chosen = this.poseChoice.get(definition.id);
    return definition.poses.find((pose) => pose.id === chosen) ?? defaultPose(definition);
  }

  /** Height first: it is the number staffage exists to communicate. */
  private sizeLine(definition: FaunaDefinition, pose: FaunaPose): string {
    const unit = this.deps.preferences.unit;
    const height = pose.heightMeters ?? definition.size.heightMeters;
    const group = definition.grouping.typical;
    const flock = group > 1 ? ` · ×${group}` : '';
    return `${formatLength(height, unit)} tall${flock}`;
  }

  private tooltip(definition: FaunaDefinition, pose: FaunaPose): string {
    const parts = [definition.scientificName ?? 'Scale figure', pose.name];
    if (definition.grouping.typical > 1) {
      parts.push(`normally ${definition.grouping.typical} together — hold Alt to place one`);
    }
    return parts.join(' · ');
  }
}
